import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, LOGO_URL } from "@/lib/api";
import Reveal from "@/components/Reveal";
import HeroFrame from "@/components/HeroFrame";
import { Seo } from "@/components/Seo";

const CATALOG_ORDER = [
  { slug: "feral", tag: "Flagship Memoir", hook: "He was never meant to bow. He was born to remember. A memoir that demands you reclaim your crown.", cta: "Reserve Your Copy" },
  { slug: "poe-dad-no-dad", tag: "Reckoning with Fatherhood", hook: "The silence fathers leave behind, and what it takes to break the cycle.", cta: "Buy Now" },
  { slug: "the-crown-path-initiative", tag: "7-Week Leadership Curriculum", hook: "A seven-week curriculum for anyone ready to become who they were always meant to be.", cta: "Reserve Your Copy" },
];

export default function Home() {
  const [books, setBooks] = useState([]);

  useEffect(() => {
    api.get("/books").then((r) => setBooks(r.data)).catch(() => {});
  }, []);

  const catalog = useMemo(
    () => CATALOG_ORDER
      .map((c) => ({ ...c, book: books.find((b) => b.slug === c.slug) }))
      .filter((c) => c.book),
    [books]
  );

  return (
    <div data-testid="home-page" className="bg-white">
      <Seo
        title="1123 Publishing Company | Independent Book Publisher"
        description="1123 Publishing is a premier independent firm specializing in memoirs and social justice literature. Led by CEO Almeer kilee Nance. Reclaiming lineage through literature."
        path=""
      />
      <HeroSection />
      <MissionStrip />
      <CatalogSection catalog={catalog} />
      <AcquiringSection />
      <FooterCTA />
    </div>
  );
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-navy text-ivory" data-testid="hero-section">
      <div className="parchment-overlay" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/0 via-navy/0 to-navy/40" />
      <HeroFrame />
      <div className="relative z-20 max-w-6xl mx-auto px-10 md:px-20 lg:px-24 py-28 md:py-36 text-center">
        <div className="overline text-gold mb-8">Independent Publishing House &middot; Est. 2025</div>
        <div className="flex justify-center mb-10">
          <img
            src={LOGO_URL}
            alt="1123 Publishing Company Logo"
            className="w-56 md:w-72 lg:w-[24rem] select-none mix-blend-screen opacity-[0.97]"
            style={{ filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.55))" }}
            data-testid="hero-logo"
          />
        </div>
        <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
          Your Story Belongs in the World.<br />
          <em className="text-gold font-normal not-italic">We'll Help You Get It There.</em>
        </h1>
        <p className="mt-8 max-w-3xl mx-auto text-ivory/85 text-lg leading-relaxed">
          1123 Publishing is an independent press built primarily for memoirists and social-justice storytellers —
          authors reclaiming their lineage and telling the truths that never made it into the official record.
          We also publish fiction, poetry, essays, and YA that carry that same spirit.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/bookstore" data-testid="hero-cta-bookstore"
            className="bg-gold text-navy px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-white transition-colors">
            Shop Our Books
          </Link>
          <Link to="/submit" data-testid="hero-cta-submit"
            className="border border-ivory/60 text-ivory px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-white hover:text-navy transition-colors">
            Submit Your Manuscript
          </Link>
        </div>
      </div>
    </section>
  );
}

function MissionStrip() {
  return (
    <section className="bg-white border-b border-navy/10 py-10" data-testid="mission-strip">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="font-serif text-xl md:text-2xl text-navy italic">
          Reclaiming lineage through literature. We publish the stories institutions overlook.
        </p>
      </div>
    </section>
  );
}

function CatalogSection({ catalog }) {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="overline mb-3">The Catalog</div>
              <h2 className="font-serif text-4xl md:text-5xl text-navy">Our Books.</h2>
            </div>
            <Link to="/bookstore" className="text-navy underline underline-offset-4 text-sm tracking-wider hover:text-gold">
              Visit the bookstore &rarr;
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 md:gap-x-14 lg:gap-x-16 gap-y-20">
          {catalog.map((entry, i) => (
            <Reveal key={entry.book.id} delay={i * 0.08}>
              <CatalogCard entry={entry} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CatalogCard({ entry }) {
  const { book, tag, cta } = entry;
  return (
    <article className="flex flex-col h-full" data-testid={`catalog-card-${book.slug}`}>
      <Link to={`/book/${book.slug}`} className="block group">
        <div className="relative aspect-[2/3] overflow-hidden bg-navy"
          style={{
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.06) inset, -3px 0 0 rgba(0,0,0,0.2), 14px 22px 40px rgba(14,35,64,0.28)",
          }}>
          <img
            src={book.cover_image}
            alt={`${book.title} by ${book.author} - Book Cover`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
          {book.coming_soon && (
            <div className="absolute top-4 right-4 bg-gold text-navy text-[10px] tracking-[0.22em] uppercase font-semibold px-2.5 py-1">
              Coming Soon
            </div>
          )}
        </div>
      </Link>
      <div className="mt-7">
        <div className="overline text-gold">{tag}</div>
        <Link to={`/book/${book.slug}`}>
          <h3 className="font-serif text-2xl md:text-3xl text-navy mt-3 leading-tight hover:text-gold transition-colors">
            {book.title}
          </h3>
        </Link>
        <div className="mt-2 text-sm text-navy/60 italic">by {book.author}</div>
        <p className="mt-4 text-charcoal/80 text-sm leading-relaxed italic">{entry.hook}</p>
        <div className="mt-6 flex items-center justify-between">
          <div className="font-serif text-xl text-navy">
            {book.coming_soon ? <span className="text-gold uppercase text-xs tracking-[0.22em]">Forthcoming</span> : `$${Number(book.price).toFixed(2)}`}
          </div>
          <Link to={`/book/${book.slug}`} data-testid={`catalog-cta-${book.slug}`}
            className="text-[11px] uppercase tracking-[0.24em] font-semibold text-navy border-b-2 border-gold pb-1 hover:text-gold transition-colors">
            {cta} &rarr;
          </Link>
        </div>
      </div>
    </article>
  );
}

function AcquiringSection() {
  return (
    <section className="py-20 md:py-28 bg-parchment border-y border-navy/10" data-testid="acquiring-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
        <Reveal>
          <div className="overline mb-3">For Authors</div>
          <h2 className="font-serif text-3xl md:text-5xl text-navy leading-tight">
            Have a Story That Needs Telling?
          </h2>
          <p className="mt-6 max-w-3xl mx-auto text-charcoal/85 leading-relaxed">
            We're actively acquiring memoirs and social-justice nonfiction from authors who've lived
            the stories most publishers won't touch — especially justice-impacted writers reclaiming
            their narrative. We're also reading fiction, poetry, essays, and YA from writers in that
            same spirit.
          </p>
          <Link to="/submit" data-testid="acquiring-cta"
            className="inline-block mt-10 bg-navy text-ivory px-8 py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal transition-colors">
            See What We Publish &rarr;
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function FooterCTA() {
  return (
    <section className="py-20 md:py-24 bg-white" data-testid="footer-cta">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="font-serif text-2xl md:text-3xl text-navy leading-snug">
          Whether you're here to read or to be read &mdash; welcome to 1123.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/bookstore" className="bg-gold text-navy px-7 py-3.5 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-navy hover:text-ivory transition-colors">
            Shop Books
          </Link>
          <Link to="/submit" className="border border-navy text-navy px-7 py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-navy hover:text-ivory transition-colors">
            Submit Your Manuscript
          </Link>
          <a href="mailto:1123publishing@gmail.com" className="border border-navy/30 text-navy px-7 py-3.5 text-[11px] uppercase tracking-[0.24em] hover:bg-navy hover:text-ivory transition-colors">
            Contact
          </a>
        </div>
      </div>
    </section>
  );
}
